import { FC, useMemo } from 'react';

import { TableContentWrapper, TableStyled, TableWrapperStyled } from '/shared/Table/styles';
import { useTableState } from '/shared/Table/useTableState';

import { ITableProps } from './interfaces';
import { GenericTableContext, IRenderGenericTable } from './context';
import { getFilterComponents } from './components/TableHeadCell/filterComponents';

export const renderGenericTable: IRenderGenericTable = (props) => <GenericTable {...props} />;

function GenericTable<D, S extends string = string, F extends string = string>(props: ITableProps<D, S, F>) {
  const state = useTableState(props);
  const { options, components, filterBy, sortBy, size, styles } = state;
  const { withToolbar, withPagination } = options;
  const { Toolbar, Pagination, Head, Body, QueryRow } = components;

  const customQueryRowComponent = props.customQueryRowComponent ?? null;
  const showQueryRow = !!customQueryRowComponent || Object.values(filterBy).length > 0 || sortBy.length > 0;
  const { tableWrapper: tableWrapperCSS, tableContentWrapper: tableContentWrapperCSS } = styles ?? {};

  const contextValue = useMemo(
    () => ({ filterComponents: getFilterComponents(renderGenericTable), renderGenericTable }),
    []
  );

  return (
    <GenericTableContext.Provider value={contextValue}>
      <TableWrapperStyled css={tableWrapperCSS}>
        {withToolbar && <Toolbar {...state} />}
        {showQueryRow && <QueryRow {...state} />}
        <TableContentWrapper css={tableContentWrapperCSS}>
          <TableStyled size={size} aria-labelledby='tableTitle'>
            <Head {...state} />
            <Body {...state} />
          </TableStyled>
        </TableContentWrapper>
        {withPagination && <Pagination {...state} />}
      </TableWrapperStyled>
    </GenericTableContext.Provider>
  );
}

GenericTable.ofType = <D, S extends string = string, F extends string = string>() => {
  return GenericTable as FC<ITableProps<D, S, F>>;
};

export { GenericTable };
